/**
 * Chargeback lifecycle stages for Normalized Chargebacks tabs.
 * Stage tabs use IS_LATEST_CASE rows only (API already filters latest).
 */

export const CHARGEBACK_STAGE = {
  ALL: 'all',
  PENDING_ACTION: 'pending_action',
  EXPIRED: 'expired',
  REJECTED: 'rejected',
  FIGHT_INITIATED: 'fight_initiated',
  RECOVERED: 'recovered',
  LOST: 'lost',
};

export const CHARGEBACK_STAGE_TABS = [
  { key: CHARGEBACK_STAGE.ALL, label: 'All Chargebacks', tone: 'default' },
  { key: CHARGEBACK_STAGE.PENDING_ACTION, label: 'Pending Action', tone: 'warning' },
  { key: CHARGEBACK_STAGE.EXPIRED, label: 'Expired', tone: 'default' },
  { key: CHARGEBACK_STAGE.REJECTED, label: 'Rejected', tone: 'default' },
  { key: CHARGEBACK_STAGE.FIGHT_INITIATED, label: 'Fight Initiated / Pending Decision', tone: 'info' },
  { key: CHARGEBACK_STAGE.RECOVERED, label: 'Recovered', tone: 'success' },
  { key: CHARGEBACK_STAGE.LOST, label: 'Lost', tone: 'default' },
];

/** Stored action when user chooses Don't Fight (replaces NO_ACTION). */
export const DONT_FIGHT_ACTION_TYPE = 'NO_ACTION_TAKEN';

/**
 * Posted when reverting a Don't Fight decision.
 * Backend clears ACTION_TYPE so the row returns to Pending Action / Expired.
 */
export const REVERT_ACTION_TYPE = 'REVERT';

const getField = (row, ...names) => {
  if (!row) return null;
  for (const name of names) {
    if (row[name] != null && row[name] !== '') return row[name];
    const upper = String(name).toUpperCase();
    if (row[upper] != null && row[upper] !== '') return row[upper];
    const lower = String(name).toLowerCase();
    if (row[lower] != null && row[lower] !== '') return row[lower];
  }
  return null;
};

const normalizeText = (value) => String(value ?? '').trim().toUpperCase();

export const getActionType = (row) => normalizeText(getField(row, 'ACTION_TYPE', 'action_type'));

export const isDontFightAction = (actionType) => {
  const a = normalizeText(actionType);
  return a === 'NO_ACTION_TAKEN' || a === 'NO_ACTION';
};

export const formatActionTypeLabel = (actionType) => {
  const a = normalizeText(actionType);
  if (!a || a === REVERT_ACTION_TYPE) return '-';
  if (isDontFightAction(a)) return "Don't Fight";
  if (a === 'FIGHT_TRANSACTION') return 'Fight Transaction';
  return actionType;
};

/** Note left when user confirmed Don't Fight (from actions table / enriched API). */
export const getDontFightNote = (row) => {
  const value = getField(
    row,
    'NOTES_ON_FIGHT_TRANSACTION',
    'notes_on_fight_transaction',
    'DONT_FIGHT_NOTE',
    'dont_fight_note'
  );
  if (value == null) return '';
  return String(value).trim();
};


const parseDateValue = (raw) => {
  if (raw == null || raw === '') return null;
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
    return new Date(raw.getFullYear(), raw.getMonth(), raw.getDate());
  }
  const s = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const [y, m, d] = s.slice(0, 10).split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  const parsed = new Date(s);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

const startOfToday = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

/**
 * Respond-by date for Expired / days-left.
 * Prefer processor DUE_DATE; if empty, use HARMONATE_ESTIMATED_DUE_DATE.
 * If both are empty, do not expire the row.
 */
export const resolveRespondByDate = (row) => {
  const processorDue = parseDateValue(getField(row, 'DUE_DATE', 'due_date'));
  if (processorDue) return processorDue;
  return parseDateValue(
    getField(row, 'HARMONATE_ESTIMATED_DUE_DATE', 'harmonate_estimated_due_date')
  );
};

/** True when the respond-by date in use is the Harmonate estimate (no processor date). */
export const isRespondByEstimate = (row) => {
  const processorDue = parseDateValue(getField(row, 'DUE_DATE', 'due_date'));
  if (processorDue) return false;
  return Boolean(
    parseDateValue(
      getField(row, 'HARMONATE_ESTIMATED_DUE_DATE', 'harmonate_estimated_due_date')
    )
  );
};

const isPastRespondBy = (row) => {
  const due = resolveRespondByDate(row);
  if (!due) return false;
  return due.getTime() < startOfToday().getTime();
};

/** Chase Provisional Win counts as pending, not recovered. */
export const isProvisionalWin = (row) => {
  const winLoss = normalizeText(getField(row, 'CHARGEBACK_WIN_LOSS', 'chargeback_win_loss'));
  return winLoss.includes('PROVISIONAL');
};

const isWon = (row) => {
  if (isProvisionalWin(row)) return false;
  const winLoss = normalizeText(getField(row, 'CHARGEBACK_WIN_LOSS', 'chargeback_win_loss'));
  if (['WIN', 'WON', 'RECOVERED', 'MERCHANT', 'FAVOR', 'FAVOUR'].some((t) => winLoss.includes(t))) {
    return true;
  }
  const status = normalizeText(getField(row, 'CHARGEBACK_STATUS', 'chargeback_status'));
  return ['RECOVERED', 'WON', 'WIN', 'REVERSED', 'REVERSAL', 'WON_BY_MERCHANT'].some((t) =>
    status.includes(t)
  );
};

const isLost = (row) => {
  const winLoss = normalizeText(getField(row, 'CHARGEBACK_WIN_LOSS', 'chargeback_win_loss'));
  if (['LOSS', 'LOST', 'ISSUER', 'CARDHOLDER'].some((t) => winLoss.includes(t))) {
    return true;
  }
  const status = normalizeText(getField(row, 'CHARGEBACK_STATUS', 'chargeback_status'));
  return ['LOST', 'LOSS', 'LOST_BY_MERCHANT', 'FORFEITED'].some((t) => status.includes(t));
};

const isExpiredStatus = (row) => {
  const status = normalizeText(getField(row, 'CHARGEBACK_STATUS', 'chargeback_status'));
  return status.includes('EXPIRED');
};

/**
 * Classify a latest-case chargeback row into one lifecycle stage.
 */
export const classifyChargebackStage = (row) => {
  if (isWon(row)) return CHARGEBACK_STAGE.RECOVERED;
  if (isLost(row)) return CHARGEBACK_STAGE.LOST;

  const action = getActionType(row);
  if (isDontFightAction(action)) return CHARGEBACK_STAGE.REJECTED;
  if (action === 'FIGHT_TRANSACTION') return CHARGEBACK_STAGE.FIGHT_INITIATED;

  // Provisional Win stays pending until it becomes Win or Loss.
  if (isProvisionalWin(row)) {
    if (isExpiredStatus(row) || isPastRespondBy(row)) return CHARGEBACK_STAGE.EXPIRED;
    return CHARGEBACK_STAGE.PENDING_ACTION;
  }

  // No decision yet (including cleared after Revert)
  if (!action || action === REVERT_ACTION_TYPE) {
    if (isExpiredStatus(row) || isPastRespondBy(row)) return CHARGEBACK_STAGE.EXPIRED;
    return CHARGEBACK_STAGE.PENDING_ACTION;
  }

  if (isExpiredStatus(row) || isPastRespondBy(row)) return CHARGEBACK_STAGE.EXPIRED;
  return CHARGEBACK_STAGE.PENDING_ACTION;
};

export const filterChargebacksByStage = (rows, stageKey) => {
  const list = Array.isArray(rows) ? rows : [];
  if (!stageKey || stageKey === CHARGEBACK_STAGE.ALL) return list;
  return list.filter((row) => classifyChargebackStage(row) === stageKey);
};

export const countChargebacksByStage = (rows) => {
  const list = Array.isArray(rows) ? rows : [];
  const counts = {
    [CHARGEBACK_STAGE.ALL]: list.length,
    [CHARGEBACK_STAGE.PENDING_ACTION]: 0,
    [CHARGEBACK_STAGE.EXPIRED]: 0,
    [CHARGEBACK_STAGE.REJECTED]: 0,
    [CHARGEBACK_STAGE.FIGHT_INITIATED]: 0,
    [CHARGEBACK_STAGE.RECOVERED]: 0,
    [CHARGEBACK_STAGE.LOST]: 0,
  };
  list.forEach((row) => {
    const stage = classifyChargebackStage(row);
    if (counts[stage] != null) counts[stage] += 1;
  });
  return counts;
};
